import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'antd'
import type { eventWithTime } from '@rrweb/types'
import { getReplaySteps } from './test-session-timeline'
import type { TestSessionSnapshot } from './test-session-types'

type VisualReplayModalProps = {
  open: boolean
  events: eventWithTime[]
  stoppedByLimit?: boolean
  initialTimeOffset?: number
  session?: TestSessionSnapshot
  onClose: () => void
}

type PlayerInstance = { $destroy?: () => void; goto?: (timeOffset: number, play?: boolean) => void }

function VisualReplayPlayer({ events, timeOffset, onReady }: { events: eventWithTime[]; timeOffset?: number; onReady: (player: PlayerInstance) => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<PlayerInstance | null>(null)
  const timeOffsetRef = useRef(timeOffset)

  useEffect(() => {
    timeOffsetRef.current = timeOffset
    if (typeof timeOffset === 'number') playerRef.current?.goto?.(timeOffset, false)
  }, [timeOffset])

  useEffect(() => {
    const container = containerRef.current
    if (!container || events.length === 0) return
    let disposed = false
    let player: PlayerInstance | undefined

    void import('rrweb-player/dist/style.css')
    void import('rrweb-player').then(({ default: Player }) => {
      if (disposed) return
      player = new Player({
        target: container,
        props: { events, autoPlay: false, showController: true },
      })
      playerRef.current = player
      if (typeof timeOffsetRef.current === 'number') player.goto?.(timeOffsetRef.current, false)
      onReady(player)
    })

    return () => {
      disposed = true
      player?.$destroy?.()
      container.replaceChildren()
    }
  }, [events, onReady])

  return <div ref={containerRef} data-testid="visual-replay-player" className="min-h-[480px] overflow-auto" />
}

export default function VisualReplayModal({ open, events, session, stoppedByLimit = false, initialTimeOffset, onClose }: VisualReplayModalProps) {
  const [selectedStep, setSelectedStep] = useState<{ initialTimeOffset?: number; timeOffset: number } | null>(null)
  const playerRef = useRef<PlayerInstance | null>(null)
  const replaySteps = session ? getReplaySteps(session) : []
  const timeOffset = selectedStep && selectedStep.initialTimeOffset === initialTimeOffset ? selectedStep.timeOffset : initialTimeOffset
  const handlePlayerReady = useCallback((player: PlayerInstance) => {
    playerRef.current = player
  }, [])

  const seek = (at: number) => {
    const firstEvent = events[0]
    if (!firstEvent) return
    const offset = Math.max(0, at - firstEvent.timestamp)
    setSelectedStep({ initialTimeOffset, timeOffset: offset })
    playerRef.current?.goto?.(offset, false)
  }
  const close = () => {
    setSelectedStep(null)
    onClose()
  }

  return <Modal rootClassName="devtools-visual-replay-modal rr-block" title="测试会话回放" open={open} onCancel={close} footer={null} width={1000} destroyOnHidden>
    {stoppedByLimit ? <div className="mb-3 text-xs text-amber-600">视觉录制已在 10 分钟后停止，后续操作仍保留在测试记录中。</div> : null}
    {open && events.length > 0 ? <div className="grid grid-cols-[minmax(0,1fr)_220px] gap-3">
      <VisualReplayPlayer events={events} timeOffset={timeOffset} onReady={handlePlayerReady} />
      <aside className="max-h-[480px] overflow-y-auto border-l border-gray-100 pl-3" aria-label="回放步骤">
        <div className="mb-2 text-xs font-medium text-gray-700">回放步骤</div>
        <div className="space-y-1">
          {replaySteps.map((step) => <Button key={step.id} type="text" size="small" className="!h-auto !w-full !justify-start !px-1 !py-1 !text-left !text-xs !whitespace-normal" onClick={() => seek(step.at)}>
            <span className={step.kind === 'problem' ? 'text-blue-600' : step.kind === 'diagnostic' ? 'text-amber-700' : 'text-gray-600'}>{step.label}</span>
          </Button>)}
          {!replaySteps.length ? <div className="text-xs text-gray-400">未记录业务步骤</div> : null}
        </div>
      </aside>
    </div> : null}
  </Modal>
}
